/**
 * Shared SQL-safety primitives for client-side CREATE TABLE builders.
 *
 * The two table builders (`CreateTableModal`, `ConnectorInputModal`) have
 * meaningfully drifted on column-default handling. This module gives them a
 * single source of truth for the security-relevant pieces so we can't
 * regress one side while tightening the other.
 */

/**
 * Table name prefixes reserved for system / per-org-schema use. Mirrors the
 * Go-side list at `internal/pkg/sqlutil.ReservedTablePrefixes`.
 */
export const RESERVED_TABLE_PREFIXES = ["pg_", "orgdb_"] as const;

/** Column types offered in the table builders. Postgres types. */
export const COLUMN_TYPES = [
  "TEXT",
  "VARCHAR",
  "INTEGER",
  "BIGINT",
  "SMALLINT",
  "REAL",
  "NUMERIC",
  "BOOLEAN",
  "DATE",
  "TIMESTAMP",
  "TIMESTAMPTZ",
  "BYTEA",
  "JSON",
  "JSONB",
  "UUID",
  "SERIAL",
  "BIGSERIAL",
  "SMALLSERIAL",
] as const;

/** Set form for O(1), case-sensitive (UPPER) membership checks. */
export const ALLOWED_COLUMN_TYPES = new Set<string>(COLUMN_TYPES);

/** Auto-incrementing integer types — implicitly NOT NULL with a sequence default. */
export const SERIAL_TYPES = new Set<string>(["SERIAL", "BIGSERIAL", "SMALLSERIAL"]);

/**
 * Allowlist of column-default expressions that are safe to inline into a
 * CREATE TABLE statement.
 *
 * The previous broad `\w+\([^)]*\)` pattern would let users smuggle calls like
 * `pg_sleep(...)` into a default expression. We instead enumerate the safe
 * forms explicitly:
 *   - parenless keywords: NULL, CURRENT_TIMESTAMP, CURRENT_DATE, CURRENT_TIME, TRUE, FALSE
 *   - numeric literals
 *   - single-quoted string literals (with doubled-quote escapes)
 *   - zero-arg functions: NOW(), CURRENT_TIMESTAMP(), CURRENT_DATE(),
 *     CURRENT_TIME(), gen_random_uuid()
 */
export const SAFE_DEFAULT_PATTERN =
  /^(?:NULL|CURRENT_TIMESTAMP|CURRENT_DATE|CURRENT_TIME|TRUE|FALSE|[+-]?\d+(?:\.\d+)?|'(?:[^']|'')*'|NOW\(\)|CURRENT_TIMESTAMP\(\)|CURRENT_DATE\(\)|CURRENT_TIME\(\)|gen_random_uuid\(\))$/i;

/**
 * Wrap a SQL identifier in double quotes and escape any embedded `"` by doubling.
 * Use this for table and column names so the generated DDL stays valid even if
 * (e.g.) a future name-validation rule loosens.
 */
export function escapeIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}
